/**
 * System status for Settings → About — a MANAGEMENT view, not infra jargon.
 *
 * The backend health endpoint reports the real services (database, redis,
 * gotenberg, …); this component maps them into the business capabilities a
 * founder actually cares about ("Records & data", "Invoice printing", …) and
 * shows a plain-language status. No service names, no latencies.
 *
 * Tone language matches the settings rail: `ok` / `warn` / `bad` semantic
 * tokens + pip chips.
 */
import { RefreshCw, Activity } from "lucide-react";
import { cn } from "@flint/ui";
import { FormCard } from "../form-card.tsx";

export type HealthStatus = "ok" | "degraded" | "down";

export interface ServiceHealth {
  key: string;
  label: string;
  status: HealthStatus;
  latencyMs?: number;
  detail?: string;
}

/**
 * Business capability → the technical service keys that back it. A
 * capability's status is the worst status across its present services.
 * `kind: "channel"` swaps the status wording to connect/disconnect language.
 */
interface Capability {
  label: string;
  hint?: string;
  serviceKeys: string[];
  kind: "core" | "channel";
}

const CAPABILITIES: Capability[] = [
  { label: "Records & data", hint: "Invoices, customers, payments", serviceKeys: ["database", "redis", "inngest"], kind: "core" },
  { label: "Files & attachments", hint: "Uploaded and stored documents", serviceKeys: ["storage"], kind: "core" },
  { label: "Invoice & document printing", hint: "Generating PDFs", serviceKeys: ["gotenberg"], kind: "core" },
  { label: "Document scanning", hint: "Reading bills and receipts", serviceKeys: ["docling"], kind: "core" },
  { label: "AI assistant", hint: "Chat and automation", serviceKeys: ["ai-gateway"], kind: "core" },
  { label: "WhatsApp", serviceKeys: ["whatsapp"], kind: "channel" },
  { label: "Telegram", serviceKeys: ["telegram"], kind: "channel" },
  { label: "Slack", serviceKeys: ["slack"], kind: "channel" },
];

const TONE: Record<HealthStatus, { dot: string; pip: string }> = {
  ok: { dot: "bg-ok", pip: "bg-ok-50 text-ok border-ok-100" },
  degraded: { dot: "bg-warn", pip: "bg-warn-50 text-warn border-warn-100" },
  down: { dot: "bg-bad", pip: "bg-bad-50 text-bad border-bad-100" },
};

/** Worst status wins across a set: down > degraded > ok. */
function worst(statuses: readonly HealthStatus[]): HealthStatus {
  if (statuses.includes("down")) return "down";
  if (statuses.includes("degraded")) return "degraded";
  return "ok";
}

/** Plain-language status word — no "degraded"/"down" jargon. */
function statusWord(status: HealthStatus, kind: Capability["kind"]): string {
  if (status === "ok") return kind === "channel" ? "Connected" : "Operational";
  if (status === "degraded") return kind === "channel" ? "Not connected" : "Limited";
  return "Unavailable";
}

interface Row {
  label: string;
  hint?: string | undefined;
  status: HealthStatus;
  word: string;
  kind: Capability["kind"];
}

function toRows(services: readonly ServiceHealth[]): Row[] {
  const byKey = new Map(services.map((s) => [s.key, s.status]));
  const rows: Row[] = [];
  for (const cap of CAPABILITIES) {
    const present = cap.serviceKeys.map((k) => byKey.get(k)).filter((s): s is HealthStatus => Boolean(s));
    if (present.length === 0) continue;
    const status = worst(present);
    rows.push({ label: cap.label, hint: cap.hint, status, word: statusWord(status, cap.kind), kind: cap.kind });
  }
  return rows;
}

function Pip({ status, children }: { status: HealthStatus; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider",
        TONE[status].pip,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", TONE[status].dot)} aria-hidden />
      {children}
    </span>
  );
}

export interface SystemStatusGridProps {
  services: readonly ServiceHealth[];
  isFetching: boolean;
  lastUpdated: Date | null;
  onRefresh: () => void;
}

export function SystemStatusGrid({ services, isFetching, lastUpdated, onRefresh }: SystemStatusGridProps) {
  const rows = toRows(services);
  const overall = worst(rows.map((r) => r.status));
  const overallWord = overall === "ok" ? "All systems operational" : overall === "degraded" ? "Running with limits" : "Service disruption";

  return (
    <FormCard
      icon={<Activity size={14} strokeWidth={2} aria-hidden />}
      title="System status"
      description="A plain-language view of what's working right now."
      bodyClassName="p-0"
      badge={
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-1 text-xs font-medium text-muted hover:bg-bg hover:text-ink disabled:opacity-50"
        >
          <RefreshCw size={12} strokeWidth={2} className={cn(isFetching && "animate-spin")} aria-hidden />
          Refresh
        </button>
      }
    >
      {/* Overall banner row */}
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <span className={cn("h-2.5 w-2.5 rounded-full", TONE[overall].dot)} aria-hidden />
        <span className="text-sm font-medium text-ink">{overallWord}</span>
      </div>

      <ul>
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between border-b border-line px-5 py-2.5 last:border-b-0">
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink leading-tight">{r.label}</p>
              {r.hint && <p className="text-xs text-muted leading-tight">{r.hint}</p>}
            </div>
            <Pip status={r.status}>{r.word}</Pip>
          </li>
        ))}
      </ul>

      {lastUpdated && (
        <p className="border-t border-line px-5 py-2.5 text-xs text-muted">
          Last checked {lastUpdated.toLocaleTimeString()}
        </p>
      )}
    </FormCard>
  );
}
